import React from 'react';
import { Box } from '@mui/material';
import {
  signalBorders,
  signalColors,
  signalDawLogos,
  signalFonts,
  signalRadii,
  signalStatus,
  signalSurfaces,
  signalText,
} from '@/theme/foundations/tokens';
import {
  DawTile,
  GlassCard,
  Mono,
  ProgressTrack,
  ScreenFrame,
  SectionLabel,
  SignalButton,
  Waveform,
} from '@/common/components/signal';
import { stemsWave } from '../fixtures';

/** The four staged files, with the state that put them in the commit. */
const STAGED: [string, string, string, string][] = [
  ['＋', signalColors.cyan, 'Renders/rough master v14.wav', 'NEW'],
  ['＋', signalColors.cyan, 'Samples/Processed/bridge_pad.wav', 'NEW'],
  ['↑', signalColors.amber, 'Nightdrive.als', 'NEWER'],
  ['−', signalColors.red, 'Samples/Imported/old_loop.wav', 'DELETED'],
];

const MENU_ITEMS = ['Play', 'Download', 'Reveal in Finder', 'Copy path'];

/** Files in the phone's Renders folder. */
const MOBILE_FILES: [string, string, boolean][] = [
  ['rough master v14.wav', '62.4 MB · PLAYING', true],
  ['rough master v13.wav', '61.8 MB', false],
  ['bridge_bounce.wav', '18.2 MB', false],
  ['v12_reference.wav', '59.4 MB', false],
];

/** Solid 520px card used by the three non-glass panels. */
const Card: React.FC<{
  children: React.ReactNode;
  width?: number;
  sx?: object;
}> = ({ children, width = 520, sx }) => (
  <Box
    sx={{
      width,
      maxWidth: '100%',
      borderRadius: `${signalRadii['3xl']}px`,
      border: `1px solid ${signalBorders.medium}`,
      background: signalSurfaces.screen,
      boxShadow: '0 30px 70px -36px rgba(0,0,0,0.95)',
      padding: '24px',
      ...sx,
    }}
  >
    {children}
  </Box>
);

/**
 * A tinted notice row. The folder prompt's three states are the same component
 * with a different tone and an optional trailing control.
 */
const Notice: React.FC<{
  glyph: string;
  status: 'stems' | 'behind';
  children: React.ReactNode;
  action?: React.ReactNode;
}> = ({ glyph, status, children, action }) => {
  const tone = signalStatus[status];
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '11px',
        border: `1px solid ${tone.border}`,
        background:
          status === 'stems'
            ? 'rgba(85,200,232,0.06)'
            : 'rgba(245,196,81,0.06)',
        borderRadius: `${signalRadii.lg}px`,
        padding: '12px 14px',
      }}
    >
      <Box component="span" sx={{ color: tone.fg, fontSize: '11px' }}>
        {glyph}
      </Box>
      <Box sx={{ flex: 1, fontSize: '12.5px', color: signalText.primary }}>
        {children}
      </Box>
      {action}
    </Box>
  );
};

/**
 * 17 — Dialogs and menus.
 *
 * The small surfaces, collected so they can be compared rather than discovered
 * one at a time. Two things they share: a destructive item is always the last
 * one and always red, and a state that is still resolving says so rather than
 * showing a disabled control with no explanation — the commit dialog keeps its
 * file list visible while uploading, so a push in flight is still legible as
 * the specific four files it is pushing.
 */
export const Screen17DialogsMenus: React.FC = () => (
  <ScreenFrame
    index="17"
    title="Dialogs and menus"
    note="COMMIT · PROMPTS · MENUS · MOBILE"
    bare
  >
    <Box
      sx={{
        display: 'flex',
        gap: '20px',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
      }}
    >
      <GlassCard
        width={520}
        padding="26px"
        sx={{
          background: 'rgba(20,23,28,0.93)',
          borderColor: 'rgba(255,255,255,0.14)',
        }}
      >
        <Box
          component="h3"
          sx={{
            margin: '0 0 6px',
            fontFamily: signalFonts.display,
            fontWeight: 800,
            fontSize: '24px',
            letterSpacing: '-0.03em',
          }}
        >
          Create new version
        </Box>
        <Box
          component="p"
          sx={{
            margin: '0 0 20px',
            fontSize: '12.5px',
            lineHeight: 1.6,
            color: signalText.secondary,
          }}
        >
          4 changed files will become v15. Up-to-date and unknown files are left
          alone.
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '7px',
            marginBottom: '18px',
          }}
        >
          <Mono component="label" size={10} weight={600} letterSpacing="0.12em">
            COMMIT MESSAGE
          </Mono>
          <Box
            sx={{
              background: 'rgba(255,106,31,0.06)',
              border: `1px solid ${signalColors.accent}`,
              borderRadius: `${signalRadii.md}px`,
              padding: '12px 14px',
              boxShadow: '0 0 0 3px rgba(255,106,31,0.18)',
              fontSize: '13.5px',
              color: signalText.primary,
            }}
          >
            Vocal comp + new bridge
          </Box>
        </Box>

        <Box
          sx={{
            border: `1px solid ${signalBorders.default}`,
            borderRadius: `${signalRadii.lg}px`,
            overflow: 'hidden',
            marginBottom: '18px',
          }}
        >
          {STAGED.map(([glyph, color, file, label], i) => (
            <Box
              key={file}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 13px',
                borderBottom:
                  i === STAGED.length - 1
                    ? 'none'
                    : `1px solid ${signalBorders.hairline}`,
                fontSize: '12.5px',
                color: signalText.primary,
              }}
            >
              <Box component="span" sx={{ color, fontSize: '11px' }}>
                {glyph}
              </Box>
              <Mono size={11.5} color={signalText.primary} sx={{ flex: 1 }}>
                {file}
              </Mono>
              <Mono size={9.5} weight={600} color={color}>
                {label}
              </Mono>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            marginBottom: '8px',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Mono size={10.5} color={signalText.secondary}>
            UPLOADING 3 OF 4
          </Mono>
          <Mono size={10.5} color={signalColors.accentBright}>
            72%
          </Mono>
        </Box>
        <ProgressTrack value={72} height={6} sx={{ marginBottom: '20px' }} />

        <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <SignalButton
            variant="ghost"
            sx={{
              padding: '10px 14px',
              borderRadius: `${signalRadii.md}px`,
              color: signalText.faint,
            }}
          >
            Cancel
          </SignalButton>
          {/* Dimmed rather than disabled — the push is happening, so the
              button reports progress instead of refusing interaction. */}
          <SignalButton
            glow={false}
            sx={{
              padding: '10px 18px',
              borderRadius: `${signalRadii.md}px`,
              opacity: 0.75,
            }}
          >
            Pushing…
          </SignalButton>
        </Box>
      </GlassCard>

      <Box
        sx={{
          width: 520,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}
      >
        <Card width={undefined}>
          <SectionLabel
            size={10}
            sx={{ display: 'block', marginBottom: '14px' }}
          >
            FOLDER PROMPT · THREE STATES
          </SectionLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Notice glyph="◴" status="stems">
              Checking for desktop app…
            </Notice>
            <Notice
              glyph="▣"
              status="stems"
              action={
                <SignalButton
                  glow={false}
                  sx={{
                    padding: '6px 11px',
                    fontSize: '11.5px',
                    borderRadius: `${signalRadii.sm}px`,
                  }}
                >
                  Locate
                </SignalButton>
              }
            >
              Select your local project folder to enable file sync
            </Notice>
            <Notice glyph="!" status="behind">
              File sync requires the{' '}
              <Box
                component="a"
                href="#"
                sx={{
                  color: signalColors.amber,
                  fontWeight: 600,
                  textDecoration: 'underline',
                }}
              >
                Desktop app
              </Box>
              .
            </Notice>
          </Box>
        </Card>

        <Card width={undefined}>
          <SectionLabel
            size={10}
            sx={{ display: 'block', marginBottom: '14px' }}
          >
            FILE ACTIONS MENU
          </SectionLabel>
          <Box sx={{ display: 'flex', gap: '18px', alignItems: 'flex-start' }}>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '1px',
                fontSize: '13px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '9px',
                  padding: '7px 10px',
                  borderRadius: `${signalRadii.sm}px`,
                  color: signalText.secondary,
                }}
              >
                <Box component="span" sx={{ color: signalColors.cyan }}>
                  ▣
                </Box>
                Renders
                <Mono size={10.5} sx={{ marginLeft: 'auto' }}>
                  9
                </Mono>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '9px',
                  padding: '7px 10px 7px 26px',
                  borderRadius: `${signalRadii.sm}px`,
                  background: 'rgba(255,106,31,0.12)',
                  color: signalText.primary,
                  fontWeight: 600,
                }}
              >
                <Box component="span" sx={{ color: signalColors.accent }}>
                  ♪
                </Box>
                rough master v14.wav
                <Box
                  component="span"
                  sx={{ marginLeft: 'auto', color: signalColors.accentBright }}
                >
                  ⋮
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '9px',
                  padding: '7px 10px 7px 26px',
                  borderRadius: `${signalRadii.sm}px`,
                  color: signalText.secondary,
                }}
              >
                <Box component="span" sx={{ color: signalColors.cyan }}>
                  ♪
                </Box>
                rough master v13.wav
              </Box>
            </Box>

            <Box
              sx={{
                width: 200,
                flex: 'none',
                border: '1px solid rgba(255,255,255,0.14)',
                borderRadius: `${signalRadii.lg}px`,
                background: signalSurfaces.glassMenu,
                boxShadow: '0 24px 50px -18px rgba(0,0,0,0.95)',
                padding: '6px',
              }}
            >
              {MENU_ITEMS.map((item, i) => (
                <Box
                  key={item}
                  sx={{
                    padding: '9px 11px',
                    borderRadius: `${signalRadii.sm}px`,
                    fontSize: '12.5px',
                    cursor: 'pointer',
                    color: i === 0 ? signalText.primary : signalText.secondary,
                    background:
                      i === 0 ? signalSurfaces.fillStrong : 'transparent',
                    '&:hover':
                      i === 0
                        ? undefined
                        : {
                            background: signalSurfaces.fillStrong,
                            color: signalText.primary,
                          },
                  }}
                >
                  {item}
                </Box>
              ))}
              <Box
                sx={{
                  height: '1px',
                  background: signalBorders.subtle,
                  margin: '5px 8px',
                }}
              />
              <Box
                sx={{
                  padding: '9px 11px',
                  borderRadius: `${signalRadii.sm}px`,
                  fontSize: '12.5px',
                  color: signalColors.red,
                  cursor: 'pointer',
                  '&:hover': { background: 'rgba(255,92,92,0.12)' },
                }}
              >
                Delete locally
              </Box>
            </Box>
          </Box>
        </Card>
      </Box>

      <Card sx={{ display: 'flex', flexDirection: 'column' }}>
        <SectionLabel size={10} sx={{ display: 'block', marginBottom: '16px' }}>
          PAYMENT METHODS
        </SectionLabel>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            marginBottom: '18px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '13px',
              border: '1px solid rgba(255,106,31,0.4)',
              background: 'rgba(255,106,31,0.07)',
              borderRadius: '13px',
              padding: '14px 15px',
            }}
          >
            <Mono
              size={9}
              weight={600}
              color={signalText.primary}
              sx={{
                width: 36,
                height: 24,
                flex: 'none',
                borderRadius: '5px',
                background: signalBorders.medium,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              VISA
            </Mono>
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  fontSize: '13px',
                  color: signalText.primary,
                  fontWeight: 600,
                }}
              >
                ···· 4417
              </Box>
              <Mono size={10} sx={{ display: 'block', marginTop: '3px' }}>
                EXPIRES 12 / 26
              </Mono>
            </Box>
            <Mono
              size={9.5}
              weight={600}
              color={signalColors.accentBright}
              sx={{
                padding: '3px 8px',
                borderRadius: '999px',
                background: 'rgba(255,106,31,0.16)',
              }}
            >
              DEFAULT
            </Mono>
            <Box
              component="span"
              sx={{ color: signalText.muted, fontSize: '13px' }}
            >
              ⋮
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '13px',
              border: `1px solid ${signalBorders.medium}`,
              borderRadius: '13px',
              padding: '14px 15px',
            }}
          >
            <Mono
              size={8}
              weight={600}
              color={signalText.secondary}
              sx={{
                width: 36,
                height: 24,
                flex: 'none',
                borderRadius: '5px',
                background: signalBorders.subtle,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              MC
            </Mono>
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  fontSize: '13px',
                  color: signalText.secondary,
                  fontWeight: 600,
                }}
              >
                ···· 8888
              </Box>
              <Mono size={10} sx={{ display: 'block', marginTop: '3px' }}>
                EXPIRES 08 / 25
              </Mono>
            </Box>
            <Mono size={10} sx={{ cursor: 'pointer' }}>
              MAKE DEFAULT
            </Mono>
            <Box
              component="span"
              sx={{ color: signalText.muted, fontSize: '13px' }}
            >
              ⋮
            </Box>
          </Box>

          <Box
            sx={{
              padding: '13px',
              borderRadius: '13px',
              border: `1px dashed ${signalBorders.control}`,
              color: signalText.muted,
              fontSize: '12.5px',
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': {
                color: signalText.primary,
                borderColor: 'rgba(255,255,255,0.28)',
              },
            }}
          >
            ＋ Add payment method
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '11px',
            border: '1px solid rgba(85,200,232,0.28)',
            background: 'rgba(85,200,232,0.06)',
            borderRadius: `${signalRadii.lg}px`,
            padding: '13px 15px',
            marginTop: 'auto',
          }}
        >
          <Box
            component="span"
            sx={{ color: signalColors.cyan, fontSize: '11px' }}
          >
            ℹ
          </Box>
          <Box
            component="span"
            sx={{
              fontSize: '12.5px',
              color: signalText.secondary,
              lineHeight: 1.5,
            }}
          >
            Cards are held by our payment provider — StudioVault never stores
            card numbers.
          </Box>
        </Box>
      </Card>

      {/* Mobile — the same file list and player at phone width, to prove the
          row and transport survive the narrower column. */}
      <Box
        sx={{
          width: 340,
          maxWidth: '100%',
          borderRadius: '32px',
          border: `1px solid ${signalBorders.strong}`,
          background: signalSurfaces.screen,
          boxShadow: '0 40px 90px -34px rgba(0,0,0,0.95)',
          padding: '12px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            borderRadius: '24px',
            overflow: 'hidden',
            background: signalSurfaces.panel,
            display: 'flex',
            flexDirection: 'column',
            height: 660,
          }}
        >
          <Box
            sx={{
              padding: '14px 16px 12px',
              borderBottom: `1px solid ${signalBorders.divider}`,
              display: 'flex',
              alignItems: 'center',
              gap: '11px',
            }}
          >
            <Box
              component="span"
              sx={{ color: signalText.secondary, fontSize: '14px' }}
            >
              ←
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  fontFamily: signalFonts.display,
                  fontWeight: 700,
                  fontSize: '16px',
                  letterSpacing: '-0.02em',
                }}
              >
                Nightdrive
              </Box>
              <Mono size={9.5} sx={{ display: 'block', marginTop: '2px' }}>
                RENDERS · 9 FILES
              </Mono>
            </Box>
            <DawTile
              src={signalDawLogos.Ableton}
              label="Ableton Live"
              size={28}
              radius={8}
            />
          </Box>

          <Box
            sx={{
              padding: '12px 14px',
              display: 'flex',
              gap: '7px',
              alignItems: 'center',
              borderBottom: `1px solid ${signalBorders.divider}`,
            }}
          >
            <Mono size={10} color={signalColors.accentBright}>
              Nightdrive
            </Mono>
            <Mono size={10}>/</Mono>
            <Mono size={10} color={signalText.primary}>
              Renders
            </Mono>
          </Box>

          <Box
            sx={{
              flex: 1,
              padding: '8px 10px',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              overflow: 'hidden',
            }}
          >
            {MOBILE_FILES.map(([name, meta, active]) => (
              <Box
                key={name}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '11px',
                  padding: '13px 12px',
                  borderRadius: '11px',
                  background: active ? 'rgba(255,106,31,0.12)' : 'transparent',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    color: active ? signalColors.accent : signalColors.cyan,
                    fontSize: '12px',
                  }}
                >
                  ♪
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      fontSize: '13.5px',
                      color: active ? signalText.primary : signalText.bright,
                      fontWeight: active ? 600 : 400,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {name}
                  </Box>
                  <Mono size={9.5} sx={{ display: 'block', marginTop: '3px' }}>
                    {meta}
                  </Mono>
                </Box>
                <Box
                  component="span"
                  sx={{
                    color: active
                      ? signalColors.accentBright
                      : signalText.faint,
                    fontSize: '14px',
                  }}
                >
                  ⋮
                </Box>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              borderTop: `1px solid ${signalBorders.subtle}`,
              padding: '14px 16px 16px',
              background: signalSurfaces.fillWeak,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '12px',
              }}
            >
              <Box
                component="button"
                type="button"
                aria-label="Pause"
                sx={{
                  width: 44,
                  height: 44,
                  flex: 'none',
                  borderRadius: '999px',
                  border: 0,
                  cursor: 'pointer',
                  background: signalColors.accent,
                  color: signalColors.onAccent,
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ❚❚
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  rough master v14.wav
                </Box>
                <Mono
                  size={10.5}
                  weight={600}
                  color={signalColors.accentBright}
                  sx={{ display: 'block', marginTop: '3px' }}
                >
                  01:42.06 / 03:58.20
                </Mono>
              </Box>
            </Box>
            <Waveform bars={stemsWave} height={34} />
          </Box>
        </Box>
      </Box>
    </Box>
  </ScreenFrame>
);

export default Screen17DialogsMenus;
